// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import {
    AvailabilityMessageType,
    CartLineModel,
    ProductSubscriptionDto,
} from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import AddToCartButton from "@steiner/App/Components/AddToCartButton/AddToCartButton";
import ProductAddedToCartMessage from "@steiner/Overrides/Components/ProductAddedToCartMessage";
import { addToCartStatus } from "@steiner/XDL/constants";
import Cart from "@steiner/XDL/Icons/Cart";
import { addToCartButtonStyles } from "@steiner/XDL/PreStyle/button";
import { parseBoolean } from "@steiner/XDL/Utilities";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ButtonPresentationProps;
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    subscription?: ProductSubscriptionDto;
    onAddToCartClick?: () => void;
    /**
     * XNG-Changes
     */
    disabled?: boolean;
    displayAsIcon?: boolean;
    overrideQty?: number;
}

export type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

const mapStateToProps = (state: ApplicationState, props: OwnProps & HasProductContext) => {
    const {
        productSettings: { showAddToCartConfirmationDialog },
        cartSettings: { addToCartPopupTimeout },
        customSettings: { steinerCartNotifyMillis } = {} as any,
    } = getSettingsCollection(state);

    const isCartLoading = getCurrentCartState(state).isLoading;
    const { isUpdatingCart } = state.context;

    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        isAddToCartButtonDisabled: isCartLoading || isUpdatingCart,
        showAddToCartConfirmationDialog,
        addToCartPopupTimeout,
        steinerCartNotifyMillis,
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
};

/**
 * XNG-Changes
 */
export interface ProductAddToCartButtonStyles extends ButtonPresentationProps {
    buttonIcon?: IconPresentationProps;
    spinner?: LoadingSpinnerProps;
}

export const productAddToCartButtonStyles: ProductAddToCartButtonStyles = {
    ...addToCartButtonStyles,
    buttonIcon: {
        color: "common.ui6",
        size: 18,
    },
    spinner: {
        color: "common.ui6",
        size: 18,
    },
};

const icons = {
    default: Cart,
};

const ProductAddToCartButton = ({
    productContext: {
        product: { id: productId, allowZeroPricing, quoteRequired },
        productInfo: { qtyOrdered, unitOfMeasure, pricing, failedToLoadInventory },
    },
    hasEnoughInventory,
    isAddToCartButtonDisabled,
    showAddToCartConfirmationDialog,
    steinerCartNotifyMillis,
    addToCart,
    canAddToCart,
    labelOverride,
    extendedStyles,
    notes,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    onAddToCartClick,
    displayAsIcon,
    disabled: disabledProps,
    overrideQty,
    ...otherProps
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeToNew(productAddToCartButtonStyles, extendedStyles));

    /**
     * XNG-Changes:
     * Add status mode to addToCart button
     */
    const [currentStatus, setCurrentStatus] = useState(addToCartStatus.default);

    /**
     * XNG-Changes:
     * Reset status mode to default
     */
    React.useEffect(() => {
        if (currentStatus === addToCartStatus.success) {
            setTimeout(() => {
                setCurrentStatus(addToCartStatus.default);
            }, 1000);
        }
    }, [currentStatus]);

    /**
     * XNG-Changes
     */
    const label = labelOverride ?? translate(currentStatus.label || "Add to Cart");

    /**
     * XNG-Changes
     * Show Button with disable mode
     */
    if ((!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart) {
        return (
            <Button {...styles} disabled={true} aria-label={labelOverride || translate("Add To Cart")} {...otherProps}>
                {displayAsIcon ? <Icon src={icons.default} {...styles.buttonIcon} /> : label}
            </Button>
        );
    }

    /**
     * XNG-Changes
     * Make disable as variable
     */
    const isDisabled = qtyOrdered <= 0 || isAddToCartButtonDisabled || disabledProps;

    const addToCartClickHandler = async () => {
        /**
         * XNG-Changes
         * Check if addToCart is disabled or not
         */
        if (isDisabled) {
            return;
        }

        onAddToCartClick?.();

        // xng-changes - allow add 0 price products
        // if (!quoteRequired && !allowZeroPricing && pricing && getUnitNetPrice(pricing, 1).price === 0) {
        //     toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
        //     return;
        // }

        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                      .filter(sectionOptionId => !!sectionOptionId)
                      .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;

        /**
         * XNG-Changes
         * Set status is loading
         */
        setCurrentStatus(addToCartStatus.loading);

        const cartline = (await addToCart({
            productId,
            // XNG-Change
            qtyOrdered: overrideQty || qtyOrdered,
            unitOfMeasure,
            notes,
            sectionOptions,
            subscription,
        })) as CartLineModel;

        /**
         * XNG-Changes
         * Set status is success
         */
        setCurrentStatus(addToCartStatus.success);

        if (showAddToCartConfirmationDialog) {
            toasterContext.addToast({
                body: (
                    <ProductAddedToCartMessage
                        isQtyAdjusted={cartline.isQtyAdjusted}
                        multipleProducts={false}
                        showItemsWillBeBackOrderMessage={(cartline.qtyOrdered || 1) > cartline.qtyOnHand}
                        isOutOfStock={cartline.availability?.messageType === AvailabilityMessageType.OutOfStock}
                        addedRequiredProducts={parseBoolean(cartline.properties?.addedRequiredProducts)}
                    />
                ),
                messageType: "success",
                // xng-change - https://xngage.atlassian.net/browse/STR-2537/
                timeoutLength: steinerCartNotifyMillis,
            });
        }
    };

    return (
        <AddToCartButton
            extendedStyles={styles}
            onClick={addToCartClickHandler}
            disabled={qtyOrdered <= 0 || isAddToCartButtonDisabled || disabledProps}
            aria-label={translate("Add to Cart")}
            currentStatus={currentStatus}
            displayAsIcon={displayAsIcon}
            label={label}
            {...otherProps}
        />
    );
};
export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductAddToCartButton));
